// Layout/logic helpers for the Stuck Refund Workflow dashboard: region-stacked chart,
// Orca bucket deep-links, SAM ticket grouping, platform-owned states.
import { StuckRow, TicketInfo, StackedEntry, TicketGroup, Rail } from './types';
import {
    STUCK_TICKET_THRESHOLD, EXACT_OP, ORCA_HOSTS, ORCA, WORKFLOW, RAIL,
    PLATFORM_PLUGIN, PLATFORM_PLUGIN_NATIVE, UNRESOLVED_PLUGINS, INVALID_PLUGINS,
} from './constants';

export const railOf = (workflow: string): Rail =>
    workflow === WORKFLOW.NATIVE ? RAIL.NATIVE : RAIL.PCES;

/** Deep-link to the Orca work-item bucket for a stuck state. */
export function bucketUrl(region: string, plugin: string, workflow: string, state: string): string {
    const host = ORCA_HOSTS[region] || ORCA_HOSTS.NA;
    if (workflow === WORKFLOW.NATIVE) {
        const { client, workflow: wf, index } = ORCA.NATIVE;
        const base = `https://${host}/#/clients/${client}/executions?workflowName=${wf}&indexName=${index}&operators=EQUALS&attributeValuesLeft=${encodeURIComponent(state)}&attributeValuesRight=`;
        // Platform (Native) has no real plugin — filter by State only, not the synthetic label.
        if (plugin === PLATFORM_PLUGIN || plugin === PLATFORM_PLUGIN_NATIVE) {
            return base;
        }
        return `${base}&operators=EQUALS&attributeValuesLeft=${encodeURIComponent(plugin)}&attributeValuesRight=`;
    }
    const { client, workflow: wf, index } = ORCA.PCES;
    return `https://${host}/#/clients/${client}/executions?workflowName=${wf}&indexName=${index}&attributeNames=State&attributeTypes=STRING&operators=EQUALS&attributeValuesLeft=${encodeURIComponent(state)}&attributeValuesRight=`;
}

/** Platform-owned = PCES-Retry ClassifyError non-exact-op state, OR a Native row whose plugin is
 * unresolved (no plugin dimension). Native rows WITH a real plugin (e.g. the always-plugin-attributed
 * WaitForNextProcess::Error::PaymentMethodRefundPending) are plugin-owned, not platform-owned. */
export const isPlatformOwned = (r: StuckRow): boolean =>
    (r.workflow === WORKFLOW.PCES && r.state.includes('::Error::') && !EXACT_OP.test(r.state))
    || (r.rail === RAIL.NATIVE && r.plugin === PLATFORM_PLUGIN_NATIVE);

/* ---------- Region-stacked chart entries ---------- */

/** Group rows by a dimension, stacking counts by region; sorted by total desc. */
export function buildStacked(rows: StuckRow[], dimFn: (r: StuckRow) => string): StackedEntry[] {
    const map = new Map<string, StackedEntry>();
    for (const r of rows) {
        const k = dimFn(r) || '(none)';
        const e = map.get(k) || { key: k, total: 0, byRegion: {} };
        e.total += r.stuck;
        e.byRegion[r.region] = (e.byRegion[r.region] || 0) + r.stuck;
        map.set(k, e);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

/* ---------- Ticket-verification groups (per region|plugin|rail) ---------- */

/**
 * Label unresolved-plugin rows as Platform instead of "(no plugin)". PMRAS/Native has no plugin
 * dimension via the Coral aggregate, so those become "Platform (Native)"; PCES unresolved → "Platform".
 */
export function resolvePlugin(rawPlugin: string | undefined, rail: Rail): string {
    if (rawPlugin && !UNRESOLVED_PLUGINS.has(rawPlugin)) return rawPlugin;
    return rail === RAIL.NATIVE ? PLATFORM_PLUGIN_NATIVE : PLATFORM_PLUGIN;
}

/**
 * SAM per-segment ticket groups: valid plugin, errored, PCES rows must be an exact op-segment.
 * Grouped by region|plugin|rail; over-threshold groups surface as candidate tickets.
 */
export function buildTicketGroups(rows: StuckRow[]): { over: TicketGroup[]; under: TicketGroup[] } {
    const g = new Map<string, TicketGroup>();
    for (const r of rows) {
        if (INVALID_PLUGINS.has(r.plugin)) continue;
        const rail = railOf(r.workflow);
        if (rail === RAIL.PCES && !EXACT_OP.test(r.state)) continue;
        const key = `${r.region}|${r.plugin}|${rail}`;
        const o = g.get(key) || { region: r.region, plugin: r.plugin, rail, stuck: 0, byOp: {} };
        o.stuck += r.stuck;
        const op = r.op && r.op !== '—' ? r.op : RAIL.NATIVE;
        o.byOp[op] = (o.byOp[op] || 0) + r.stuck;
        g.set(key, o);
    }
    const all = Array.from(g.values());
    const pluginTotal: Record<string, number> = {};
    all.forEach((x) => { pluginTotal[x.plugin] = (pluginTotal[x.plugin] || 0) + x.stuck; });
    const railRank: Record<string, number> = { PCES: 0, Native: 1 };
    const over = all.filter((x) => x.stuck > STUCK_TICKET_THRESHOLD).sort((a, b) => {
        if (pluginTotal[b.plugin] !== pluginTotal[a.plugin]) return pluginTotal[b.plugin] - pluginTotal[a.plugin];
        if (a.plugin !== b.plugin) return a.plugin < b.plugin ? -1 : 1;
        if (railRank[a.rail] !== railRank[b.rail]) return railRank[a.rail] - railRank[b.rail];
        return b.stuck - a.stuck;
    });
    const under = all.filter((x) => x.stuck <= STUCK_TICKET_THRESHOLD).sort((a, b) => b.stuck - a.stuck);
    return { over, under };
}


/** SIM-T search deep-link for a plugin's SAM stuck-workflow tickets (fallback when none matched). */
export function ticketSearchUrl(plugin: string, rail: Rail): string {
    const q = rail === RAIL.NATIVE
        ? `"PaymentMethodRefundPending:${plugin}"`
        : `"ClassifyError::Error::${plugin}"`;
    return 'https://t.corp.amazon.com/issues/?q=' + encodeURIComponent(q);
}

/**
 * Merge backend SIM-T ticket enrichment into ticket groups, keyed by region|rail|plugin.
 * Matched groups get the ticket fields (found/status/severity/link + full ticketList); unmatched
 * groups fall back to a SIM-T search deep-link. Mutates + returns groups.
 */
export function applyTickets(groups: TicketGroup[], tickets?: TicketInfo[]): TicketGroup[] {
    const byKey = new Map<string, TicketInfo>();
    (tickets || []).forEach((t) => byKey.set(`${t.region}|${t.rail}|${t.plugin}`, t));
    for (const g of groups) {
        const t = byKey.get(`${g.region}|${g.rail}|${g.plugin}`);
        if (t) {
            g.ticketFound = t.ticketFound;
            g.ticketCount = t.ticketCount;
            g.ticketId = t.ticketId;
            g.ticketStatus = t.ticketStatus;
            g.ticketGroup = t.ticketGroup;
            g.ticketSeverity = t.ticketSeverity;
            g.ticketUrl = t.ticketUrl;
            g.simSearchUrl = t.simSearchUrl;
            g.ticketList = t.ticketList;
        } else {
            g.simSearchUrl = ticketSearchUrl(g.plugin, g.rail);
        }
    }
    return groups;
}
